/**
 * Análisis (rc98) — hooks del módulo de resúmenes ejecutivos.
 * Solo lectura. Filtro opcional por empresa (consolidado = sin filtro).
 */
import { useQuery } from "@tanstack/react-query";
import { apiCall } from "@/lib/api";

const KEY = "analisis";

// ── Tipos ────────────────────────────────────────────────────────────────
export type ResumenEjecutivo = {
  empresa_id: number | null;
  hoy: string;
  mes_actual: {
    mes: string;        // "YYYY-MM"
    altas: number;
    bajas: number;
    neto: number;
    recaudado: string;  // decimal
  };
  mes_anterior: {
    mes: string;
    altas: number;
    bajas: number;
    neto: number;
    recaudado_total: string;
    recaudado_mismo_corte: string;
  };
  delta_recaudado_pct: number;
  cartera_vencida: {
    monto: string;
    facturas: number;
    clientes_morosos: number;
  };
  contratos: {
    activos: number;
    suspendidos: number;
    pendientes: number;
    error: number;
  };
};

export type CrecimientoMes = {
  mes: string;             // "YYYY-MM"
  altas: number;
  bajas: number;
  neto: number;
  activos_cierre: number;
  churn_pct: number;
  clientes_nuevos: number;
  mes_en_curso: boolean;
};

export type CrecimientoResponse = {
  meses: number;
  empresa_id: number | null;
  serie: CrecimientoMes[];
  // rc99 — contratos vivos sin fecha_activacion (migrados): cuentan en el
  // baseline, no como altas de ningún mes
  sin_fecha_activacion: number;
};

// rc99 — drill-down de un mes (altas o bajas)
export type DetalleMesItem = {
  contrato_id: string;
  codigo: string;
  estatus: string;
  fecha: string;
  motivo_cancelacion: string | null;
  cliente_id: string;
  cliente_codigo: string;
  cliente_nombre: string;
  plan_nombre: string;
  /** rc141 — solo en altas: false = el cliente ya tenía otro contrato antes */
  cliente_nuevo: boolean | null;
  /** rc141 — fecha del contrato más viejo del cliente (si no es nuevo) */
  cliente_desde: string | null;
};

export type DetalleMesResponse = {
  mes: string;
  tipo: "altas" | "bajas";
  empresa_id: number | null;
  total: number;
  items: DetalleMesItem[];
};

// rc99 — drill-down de morosos hoy
export type MorosoItem = {
  cliente_id: string;
  cliente_codigo: string;
  cliente_nombre: string;
  facturas: number;
  monto: string;
  dias_vencido_max: number;
};

export type MorososResponse = {
  empresa_id: number | null;
  hoy: string;
  total_clientes: number;
  total_monto: string;
  items: MorosoItem[];
};

// ── Hooks ────────────────────────────────────────────────────────────────
function empresaParam(empresaId: number | null): Record<string, number> {
  return empresaId !== null ? { empresa_id: empresaId } : {};
}

export function useResumenEjecutivo(empresaId: number | null) {
  return useQuery({
    queryKey: [KEY, "resumen", empresaId],
    queryFn: () =>
      apiCall<ResumenEjecutivo>("get", "/api/v1/analisis/resumen", {
        searchParams: empresaParam(empresaId),
      }),
    staleTime: 60_000,
  });
}

export function useCrecimiento(meses: number, empresaId: number | null) {
  return useQuery({
    queryKey: [KEY, "crecimiento", meses, empresaId],
    queryFn: () =>
      apiCall<CrecimientoResponse>("get", "/api/v1/analisis/crecimiento", {
        searchParams: { meses, ...empresaParam(empresaId) },
      }),
    staleTime: 60_000,
  });
}

/** rc99 — quiénes fueron las altas/bajas de un mes. Solo consulta con modal abierto. */
export function useDetalleMes(
  mes: string | null, tipo: "altas" | "bajas", empresaId: number | null,
) {
  return useQuery({
    queryKey: [KEY, "detalle-mes", mes, tipo, empresaId],
    queryFn: () =>
      apiCall<DetalleMesResponse>("get", "/api/v1/analisis/crecimiento/detalle", {
        searchParams: { mes: mes as string, tipo, ...empresaParam(empresaId) },
      }),
    enabled: mes !== null,
    staleTime: 60_000,
  });
}

/** rc99 — clientes morosos hoy (drill-down de cartera vencida). */
export function useMorosos(empresaId: number | null, enabled: boolean) {
  return useQuery({
    queryKey: [KEY, "morosos", empresaId],
    queryFn: () =>
      apiCall<MorososResponse>("get", "/api/v1/analisis/morosos", {
        searchParams: empresaParam(empresaId),
      }),
    enabled,
    staleTime: 60_000,
  });
}
